"use client";
import { type ComponentType, useEffect, useRef, useState } from "react";
import { type Step } from "./surveySteps";
import { Option } from "./surveySteps";
import {
  BadgeCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Package,
  Phone,
  Rocket,
  Search,
  ShoppingCart,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  getSurveySteps,
  postSurveyFill,
} from "../services/survey/surveyService";
import { useNavigate } from "react-router-dom";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";
import { ONBOARDING_PHONE_COUNTRIES } from "../const/worldCountryCodes";
import { decodeTokenService } from "../services/decodeTokenService";
import { LOCAL_ONBOARDING_STEPS } from "./localOnboardingSteps";

const DEFAULT_SURVEY_COUNTRY = ONBOARDING_PHONE_COUNTRIES.find((c) => c.code === "CO")!;
const REQUIRED_PHONE_DIGITS_BY_COUNTRY: Record<string, { length: number; startsWith?: string }> = {
  CO: { length: 10, startsWith: "3" },
  MX: { length: 10 },
  US: { length: 10 },
  EC: { length: 9 },
  PE: { length: 9 },
  CL: { length: 9 },
  AR: { length: 10 },
  BO: { length: 8 },
  BR: { length: 11 },
  CA: { length: 10 },
  PY: { length: 9 },
  UY: { length: 8 },
  VE: { length: 10 },
};

const translate = (key: string | undefined, language: Language, fallback = "") => {
  if (!key) return fallback;
  return dictionary[key]?.[language] || fallback || key;
};

type OptionIcon = ComponentType<{ className?: string }>;

const getOptionIcon = (stepId: string, value: Option["value"]): OptionIcon => {
  const optionValue = String(value);

  if (stepId === "businessModel") {
    if (optionValue === "dropshipping") return Package;
    if (optionValue === "ecommerce") return ShoppingCart;
    if (optionValue === "own_brand") return BadgeCheck;
  }

  if (stepId === "sellingTime") {
    if (optionValue === "starting") return Rocket;
    if (optionValue === "scaling") return TrendingUp;
    if (optionValue === "power") return Zap;
  }

  if (stepId === "monthlySales") return CircleDollarSign;

  return Check;
};

interface InitialSurveyProps {
  missingStepIds?: string[];
  initialAnswers?: Record<string, unknown>;
  onComplete?: () => void;
}

interface PersonalInfoValue {
  fullName: string;
  phoneNumber: string;
}

export default function InitialSurvey({
  missingStepIds = [],
  initialAnswers = {},
  onComplete,
}: InitialSurveyProps) {
  const [currentStepId, setCurrentStepId] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);
  const [surveySteps, setSurveySteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, unknown>>(initialAnswers);
  const [saving, setSaving] = useState(false);
  const { language } = useAuth();

  const isResuming = missingStepIds.length > 0;
  const userId = decodeTokenService.getUserIdFromToken() || localStorage.getItem("user_id") || "";

  // Load all steps
  useEffect(() => {
    const setInitialSteps = (allSteps: Step[]) => {
      setSurveySteps(allSteps);

      // Determine first step to show
      let firstStep: Step | undefined;
      if (isResuming) {
        firstStep = allSteps.find((s) => s.id === missingStepIds[0]);
      }
      if (!firstStep) {
        firstStep =
          allSteps.find((s: Step) => s.is_first_step) ||
          allSteps.find((s: Step) => s.id === "fullName") ||
          allSteps.find((s: Step) => s.id === "mobelNumber") ||
          allSteps[0];
      }
      if (firstStep) {
        setCurrentStepId(firstStep.id);
      }
    };

    getSurveySteps("default")
      .then((steps) => {
        const allSteps = steps.data as Step[];
        setInitialSteps(allSteps.length > 0 ? allSteps : LOCAL_ONBOARDING_STEPS);
      })
      .catch((error) => {
        console.warn("Using local onboarding steps because survey API is unavailable", error);
        setInitialSteps(LOCAL_ONBOARDING_STEPS);
      });
  }, []);

  // Sync currentStep when currentStepId changes
  useEffect(() => {
    if (surveySteps.length > 0 && currentStepId) {
      setCurrentStep(surveySteps.find((s) => s.id === currentStepId));
    }
  }, [surveySteps, currentStepId]);

  if (!currentStep) return null;

  const saveAnswers = async (newAnswers: Record<string, unknown>) => {
    try {
      setSaving(true);
      await postSurveyFill(
        {
          entity_type: "user",
          entity_id: userId,
          answers: newAnswers,
        },
        "default"
      );
      setAnswers((currentAnswers) => ({ ...currentAnswers, ...newAnswers }));
    } catch (err) {
      console.error("Error saving answer:", err);
    } finally {
      setSaving(false);
    }
  };

  // Save a single answer to the backend immediately
  const saveAnswer = async (stepId: string, value: any) => {
    await saveAnswers({ [stepId]: value });
  };

  // Get the summary step
  const getSummaryStep = () =>
    surveySteps.find((s) => s.type === "summary");

  // Determine the natural next step from the chain
  const getChainNextId = (step: Step, value: any): string | undefined => {
    if (step.options) {
      const selectedOpt = step.options.find((opt) => opt.value === value);
      if (selectedOpt?.next) return selectedOpt.next;
    }
    if (typeof value === "object" && value?.next) return value.next;
    if (typeof step.next_step === "string" && step.next_step) return step.next_step;
    return undefined;
  };

  // Find the next unanswered step walking the chain from nextId
  const findNextUnanswered = (
    startId: string | undefined,
    answered: Set<string>,
    knownAnswers: Record<string, unknown>
  ): string | undefined => {
    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const stepsById = new Map(surveySteps.map((s) => [s.id, s]));
    let id = startId;

    // Walk the selected branch, skipping steps whose saved answers we know.
    while (id && id !== summaryId) {
      if (!answered.has(id)) return id; // found an unanswered step
      const step = stepsById.get(id);
      if (!step) break;
      id = getChainNextId(step, knownAnswers[id]);
    }

    return id; // either summaryId or undefined
  };

  const handleAnswer = async (value: any) => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 1. Save this answer to the backend immediately
    await saveAnswer(currentStep.id, value);

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const newAnswers = { ...answers, [currentStep.id]: value };
    const newAnswered = new Set(Object.keys(newAnswers));

    // 2. Determine next step
    let nextId: string | undefined;

    if (isResuming) {
      // Resume mode: find the next missing step in the missingStepIds list
      const currentIdx = missingStepIds.indexOf(currentStep.id);
      const nextMissingId = currentIdx !== -1
        ? missingStepIds[currentIdx + 1]
        : missingStepIds.find((id) => !newAnswered.has(id));

      const chainNext = getChainNextId(currentStep, value);
      nextId =
        nextMissingId ||
        findNextUnanswered(chainNext, newAnswered, newAnswers) ||
        summaryId;
    } else {
      // Full mode: follow the chain, skip answered steps
      const chainNext = getChainNextId(currentStep, value);
      nextId = findNextUnanswered(chainNext, newAnswered, newAnswers);
      if (!nextId) nextId = summaryId;
    }

    // 3. Navigate
    if (nextId === summaryId || nextId === "summary") {
      // Send summary=true to mark survey as complete
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (summaryStep) {
        setHistory((prev) => [...prev, currentStep.id]);
        setCurrentStepId(summaryId);
      }
      return;
    }

    if (nextId) {
      setHistory((prev) => [...prev, currentStep.id]);
      setCurrentStepId(nextId);
    }
  };

  const handlePersonalInfoSubmit = async ({ fullName, phoneNumber }: PersonalInfoValue) => {
    await new Promise((resolve) => setTimeout(resolve, 350));

    const phoneStep =
      surveySteps.find((s) => s.id === "mobelNumber") ||
      surveySteps.find((s) => s.type === "phone");
    const phoneStepId = phoneStep?.id || "mobelNumber";
    const profileAnswers = {
      fullName,
      [phoneStepId]: phoneNumber,
    };

    await saveAnswers(profileAnswers);

    const summaryStep = getSummaryStep();
    const summaryId = summaryStep?.id || "summary";
    const newAnswers = { ...answers, ...profileAnswers };
    const newAnswered = new Set(Object.keys(newAnswers));
    const chainNext = phoneStep
      ? getChainNextId(phoneStep, phoneNumber)
      : getChainNextId(currentStep, fullName);
    const nextId = findNextUnanswered(chainNext, newAnswered, newAnswers) || summaryId;

    if (nextId === summaryId || nextId === "summary") {
      await postSurveyFill(
        { entity_type: "user", entity_id: userId, answers: { summary: true } },
        "default"
      ).catch(() => {});

      if (summaryStep) {
        setHistory((prev) => [...prev, currentStep.id]);
        setCurrentStepId(summaryId);
      }
      return;
    }

    setHistory((prev) => [...prev, currentStep.id]);
    setCurrentStepId(nextId);
  };

  const handleBack = () => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const prevStepId = newHistory.pop();
      if (prevStepId) setCurrentStepId(prevStepId);
      return newHistory;
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto">
      {/* Background Gradient - Fixed to cover entire viewport */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.2) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-10">
        <div className="w-full max-w-[560px] mx-auto bg-white/80 border border-white rounded-[28px] shadow-[0_18px_60px_rgba(49,71,176,0.12)] px-5 py-6 sm:px-8 sm:py-9 text-center">
          {currentStep.id === "fullName" ? (
            <PersonalInfoStep
              language={language}
              onSubmit={handlePersonalInfoSubmit}
              saving={saving}
            />
          ) : (
            <>
              {currentStep.type !== "summary" && (
                <h1 className="font-bold text-[26px] sm:text-[32px] md:text-[36px] leading-[1.12] mb-3 sm:mb-4 text-center sm:text-start text-[#111827]">
                  {translate(currentStep.question_text, language)}
                </h1>
              )}
              {currentStep.description && (
                <p className="text-base sm:text-lg text-[#3D3D3D] mb-6 sm:mb-7 text-center sm:text-start">
                  {translate(currentStep.description, language)}
                </p>
              )}

              {currentStep.type === "single" && currentStep.options && (
                <SingleSelectStep
                  stepId={currentStep.id}
                  options={currentStep.options}
                  onSubmit={(value) => handleAnswer(value)}
                  language={language}
                  saving={saving}
                />
              )}

              {currentStep.type === "multi" && currentStep.options && (
                <MultiSelectStep
                  options={currentStep.options}
                  onSubmit={(values) => handleAnswer(values)}
                  language={language}
                />
              )}

              {currentStep.type === "text" && (
                <TextStep
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep.type === "textarea" && (
                <TextAreaStep
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep.type === "phone" && (
                <PhoneStep
                  language={language}
                  key={currentStep.id.toString()}
                  onSubmit={(value) => handleAnswer(value)}
                />
              )}

              {currentStep.type === "summary" && (
                <SummaryStep language={language} onComplete={onComplete} />
              )}
            </>
          )}

          {currentStep.type !== "summary" && history.length > 0 && (
            <div
              onClick={handleBack}
              className="mt-4 sm:mt-6 flex items-center justify-center sm:justify-start gap-2 cursor-pointer hover:text-black transition-all"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <p className="text-sm sm:text-base md:text-lg font-medium text-gray-500">
                {translate("back", language, "Volver")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleSelectStep({
  stepId,
  options = [],
  onSubmit,
  language,
  saving,
}: {
  stepId: string;
  options: Step["options"];
  onSubmit: (value: Option["value"]) => void;
  language: Language;
  saving: boolean;
}) {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  return (
    <div className="flex flex-col gap-3">
      {options.map((opt) => (
        <ButtonAnswer
          key={opt.value.toString()}
          onClick={() => setSelectedOption(opt)}
          option={opt}
          language={language}
          selected={selectedOption?.value === opt.value}
          icon={getOptionIcon(stepId, opt.value)}
        />
      ))}
      <ButtonContinue
        onClick={() => selectedOption && onSubmit(selectedOption.value)}
        disabled={!selectedOption || saving}
        text={saving ? "Guardando..." : translate("continue", language, "Continuar")}
      />
    </div>
  );
}

const ButtonAnswer = ({
  onClick,
  option,
  language,
  selected,
  icon: Icon,
}: {
  onClick: () => void;
  option: Option;
  language: Language;
  selected: boolean;
  icon: OptionIcon;
}) => {
  const label = translate(option.label, language);
  const description =
    option.description && translate(option.description, language);

  return (
    <button
      onClick={onClick}
      className={`
                w-full
                flex
                items-center
                gap-4
                text-left
                py-4
                sm:py-5
                px-4 
                sm:px-5
                hover:shadow-md 
                active:scale-95
                text-black 
                rounded-2xl
                border 
                border-[#D9DDE8]
                transition-all 
                duration-200
                ${
                  selected
                    ? "bg-[#2F36D9] text-white border-[#2F36D9]"
                    : "bg-white hover:border-[#2F36D9]/45"
                }
            `}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${
          selected
            ? "border-white/20 bg-white/16 text-white"
            : "border-[#D9DDE8] bg-[#F7F8FF] text-[#3D3D3D]"
        }`}
      >
        <Icon className="h-6 w-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg sm:text-xl font-bold leading-tight">{label}</span>
        {description && (
        <span
          className={`mt-1 block text-sm sm:text-base leading-snug ${
            selected ? "text-white/82" : "text-[#3D3D3D]"
          }`}
        >
          {description}
        </span>
        )}
      </span>
    </button>
  );
};

interface MultiSelectStepProps {
  options: Step["options"];
  onSubmit: (values: string[]) => void;
  language: Language;
}

function MultiSelectStep({
  options = [],
  onSubmit,
  language,
}: MultiSelectStepProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggleOption = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {options.map((opt) => (
        <button
          key={opt.value.toString()}
          onClick={() => toggleOption(opt.value.toString())}
          className={`w-full border py-3 sm:py-4 md:py-6 lg:py-8 px-4 sm:px-5 md:px-6 rounded-xl transition-all duration-200 active:scale-95 text-start text-base sm:text-lg md:text-xl lg:text-2xl font-bold hover:shadow-md ${
            selected.includes(opt.value.toString())
              ? "bg-[#0E172A] text-white"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          {translate(opt.label, language)}
        </button>
      ))}
      <ButtonContinue
        onClick={() => onSubmit(selected)}
        disabled={selected.length === 0}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

function PersonalInfoStep({
  onSubmit,
  language,
  saving,
}: {
  onSubmit: (value: PersonalInfoValue) => void;
  language: Language;
  saving: boolean;
}) {
  const [fullName, setFullName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SURVEY_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = ONBOARDING_PHONE_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const nameLooksComplete = fullName.trim().split(/\s+/).length >= 2;
  const digitsOnly = phoneInput.replace(/\D/g, "");
  const countryRule = REQUIRED_PHONE_DIGITS_BY_COUNTRY[selectedCountry.code];
  const hasValidKnownCountryNumber = countryRule
    ? digitsOnly.length === countryRule.length &&
      (!countryRule.startsWith || digitsOnly.startsWith(countryRule.startsWith))
    : digitsOnly.length >= 7 && digitsOnly.length <= 15;
  const validationMessage = countryRule
    ? selectedCountry.code === "CO"
      ? "Para Colombia, el WhatsApp debe tener 10 dígitos y empezar por 3."
      : `El número debe tener ${countryRule.length} dígitos para ${selectedCountry.name}.`
    : "Escribe un número válido, entre 7 y 15 dígitos.";
  const showNameMessage = fullName.trim().length > 0 && !nameLooksComplete;
  const showValidationMessage = digitsOnly.length > 0 && !hasValidKnownCountryNumber;
  const phoneNumber = digitsOnly
    ? `${selectedCountry.dialCode} ${digitsOnly}`
    : "";
  const isReady = nameLooksComplete && hasValidKnownCountryNumber && !saving;

  return (
    <div className="text-left">
      <h1 className="font-extrabold text-[28px] sm:text-[34px] leading-[1.12] mb-8 text-[#111827]">
        Cuéntanos quién eres
      </h1>

      <div className="flex flex-col gap-5">
        <label className="flex flex-col gap-2">
          <span className="text-base sm:text-lg font-semibold text-[#3D3D3D]">
            Tu nombre completo
          </span>
          <input
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={`w-full bg-white px-4 py-3.5 sm:py-4 text-base sm:text-lg border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
              showNameMessage ? "border-[#F05A5A]" : "border-[#D9DDE8]"
            }`}
            placeholder="¿Cómo te llamas?"
          />
          {showNameMessage && (
            <span className="text-sm font-medium text-[#D64545]">
              Escribe tu nombre y apellido.
            </span>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-base sm:text-lg font-semibold text-[#3D3D3D]">
            WhatsApp
          </span>
          <div className="flex gap-2">
            <div ref={dropdownRef} className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                className="h-full min-w-[64px] rounded-2xl border border-[#D9DDE8] bg-white px-3 py-3 sm:py-4 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#2F36D9]"
                aria-label="Seleccionar indicativo"
              >
                <span
                  className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`}
                  style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        ref={searchRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={translate("search-country-placeholder", language, "Buscar país o código...")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F36D9]"
                      />
                    </div>
                  </div>

                  <ul className="max-h-52 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((c) => (
                        <li key={c.code}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCountry(c);
                              setDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                              c.code === selectedCountry.code ? "bg-gray-100" : ""
                            }`}
                          >
                            <span
                              className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`}
                              style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                            />
                            <span className="flex-1 truncate text-gray-800">{c.name}</span>
                            <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-4 py-4 text-sm text-gray-400 text-center">
                        {translate("no-countries-found", language, "No se encontraron países")}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <input
              type="tel"
              autoComplete="tel-national"
              inputMode="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d\s-]/g, ""))}
              className={`w-full bg-white px-4 py-3.5 sm:py-4 text-base sm:text-lg border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
                showValidationMessage ? "border-[#F05A5A]" : "border-[#D9DDE8]"
              }`}
              placeholder="300 000 0000"
            />
          </div>
          {showValidationMessage && (
            <span className="text-sm font-medium text-[#D64545]">
              {validationMessage}
            </span>
          )}
        </label>
      </div>

      <ButtonContinue
        onClick={() => onSubmit({ fullName: fullName.trim(), phoneNumber })}
        disabled={!isReady}
        text={saving ? "Guardando..." : translate("continue", language, "Continuar")}
      />
    </div>
  );
}

interface TextAreaStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextAreaStep({ onSubmit, language }: TextAreaStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <textarea
        className="border rounded-lg p-3 sm:p-4 text-sm sm:text-base w-full h-[150px] sm:h-[180px] md:h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          translate("write-your-answer-here", language, "Escribe tu respuesta aquí...")
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

interface TextStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextStep({ onSubmit, language }: TextStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <input
        type="text"
        className="border rounded-2xl p-4 text-base sm:text-lg w-full focus:outline-none focus:ring-2 focus:ring-[#2F36D9] bg-white border-[#D9DDE8]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          translate("write-your-answer-here", language, "Escribe tu respuesta aquí...")
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

function SummaryStep({
  language,
  onComplete,
}: {
  language: Language;
  onComplete?: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center justify-center bg-[#0E172A] rounded-full p-3 sm:p-4 mb-4 sm:mb-6">
        <Check className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-white" />
      </div>
      <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-6 sm:mb-8 text-[#0E172A] text-center">
        {translate("all-set", language, "Todo listo")}
      </h1>
      <ButtonContinue
        onClick={() => {
          if (onComplete) {
            onComplete();
            return;
          }
          if (window.innerWidth < 768) {
            navigate("/settings");
          } else {
            navigate("/home");
          }
        }}
        disabled={false}
        text={translate("go-to-account", language, "Ir a mi cuenta")}
      />
    </div>
  );
}

interface PhoneStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function PhoneStep({ onSubmit, language }: PhoneStepProps) {
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SURVEY_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = ONBOARDING_PHONE_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const digitsOnly = phoneInput.replace(/\D/g, "");
  const countryRule = REQUIRED_PHONE_DIGITS_BY_COUNTRY[selectedCountry.code];
  const hasValidKnownCountryNumber = countryRule
    ? digitsOnly.length === countryRule.length &&
      (!countryRule.startsWith || digitsOnly.startsWith(countryRule.startsWith))
    : digitsOnly.length >= 7 && digitsOnly.length <= 15;
  const validationMessage = countryRule
    ? selectedCountry.code === "CO"
      ? "Para Colombia, el WhatsApp debe tener 10 dígitos y empezar por 3."
      : `El número debe tener ${countryRule.length} dígitos para ${selectedCountry.name}.`
    : "Escribe un número válido, entre 7 y 15 dígitos.";
  const showValidationMessage = digitsOnly.length > 0 && !hasValidKnownCountryNumber;
  const phoneNumber = digitsOnly
    ? `${selectedCountry.dialCode} ${digitsOnly}`
    : "";

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="flex gap-2">
        {/* Country code dropdown */}
        <div ref={dropdownRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="h-full bg-white px-3 py-3 sm:py-4 border border-gray-200 rounded-xl flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0E172A] min-w-[64px]"
          >
            <span
              className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`}
              style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
              {/* Search */}
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      translate("search-country-placeholder", language, "Buscar país o código...")
                    }
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
                  />
                </div>
              </div>

              {/* Country list */}
              <ul className="max-h-52 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => (
                    <li key={c.code}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCountry(c);
                          setDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                          c.code === selectedCountry.code ? "bg-gray-100" : ""
                        }`}
                      >
                        <span
                          className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`}
                          style={{ width: "1.4em", height: "1.05em", display: "inline-block" }}
                        />
                        <span className="flex-1 truncate text-gray-800">{c.name}</span>
                        <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-4 text-sm text-gray-400 text-center">
                    {translate("no-countries-found", language, "No se encontraron países")}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <div className="relative flex-1">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d\s-]/g, ""))}
            className={`w-full bg-white pl-12 pr-4 py-3 sm:py-4 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2F36D9] ${
              showValidationMessage ? "border-[#F05A5A]" : "border-gray-200"
            }`}
            placeholder={
              translate("phone-number-placeholder", language, "300 123 4567")
            }
          />
        </div>
      </div>
      {showValidationMessage && (
        <p className="text-left text-sm font-medium text-[#D64545]">{validationMessage}</p>
      )}

      <ButtonContinue
        onClick={() => onSubmit(phoneNumber)}
        disabled={!hasValidKnownCountryNumber}
        text={translate("continue", language, "Continuar")}
      />
    </div>
  );
}

const ButtonContinue = ({
  onClick,
  disabled,
  text = "Continuar",
}: {
  onClick: () => void;
  disabled: boolean;
  text?: string;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-2 mt-4 sm:mt-6 bg-[#2F36D9] hover:bg-[#252AB8] active:bg-[#1F249B] text-base sm:text-lg font-bold text-white py-3.5 sm:py-4 px-5 sm:px-7 w-full rounded-2xl disabled:opacity-40 disabled:hover:scale-100 transition-all duration-300 hover:scale-[1.02] active:scale-95"
    >
      <span>{text}</span>
      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white flex-shrink-0" />
    </button>
  );
};
